import React, { useEffect, useState } from 'react'
import LexicalTextEditor from '@/components/inputs/TextEditor/LexicalTextEditor';
import styled from "styled-components";
import { useQueryClient } from 'react-query';
import { getVal, setVal, SkeletonLoader, TextInput } from 'oolib';
import { WidgetHeader } from '../WidgetBarChart/WIDGET_ELEM_ConfigForm';
import { useUpdateLoadedWidgetsTracker } from '../../../ReportLandingContext/useUpdateLoadedWidgetsTracker';
import OverlayScrollbar from '@/components/UI/OverlayScrollbar';
import { useGenerateSummary } from '@/components/analysisModules/AnnotationExplorer/comps/Summary';
import { WidgetErrorState } from '../../WidgetRenderer/comps/altStatePlaceholders';
import { useGetAISummary } from '../utils/useGetAISummary';

const StyledHeader = styled.div`
  width: 100%;
  padding: 1rem 1.6rem;
  text-align: left;
  background: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const Component = (props) => {


  useUpdateLoadedWidgetsTracker({ status: 'success', widgetIdx: props.widgetIdx });

  return (
    <div style={{ maxHeight: "525px", overflowY: "auto" }}>
      <OverlayScrollbar>
        {/* Cap the measure: a text block spanning the whole 1200px canvas is
            past comfortable reading length. The widget keeps its full width;
            only the text inside is capped. */}
        <div style={{ maxWidth: "860px" }}>
          <LexicalTextEditor
            key={props.value ? JSON.stringify(props.value) : 'no-value'}
            readOnly
            // Same content typography as PublishedPageV3's body fields ($v2 block in
            // the editor's styled.js). Only the type treatment — none of PPV3's
            // annotation wiring (onAnnotationClick / activeAnnoIds / in-place
            // annotate), which a report's text widget has no use for.
            v2
            // value={value}
            {...props}
          />
        </div>
      </OverlayScrollbar>
    </div>  
  )
}

export const optionDef = { 
  display: "Text", 
  icon: "TextAa", 
  value: "richText", 
  comp: 'WidgetRichText',
}

export const defaultConfig = {
  props: {
    readOnly: { 
      disableHeader: true,
      container: { 
        style: { backgroundColor: "transparent", border: 'none', padding: 0 } 
      },
      widgetWrapper: {
        style: {
          padding: `1rem 1rem 1.8rem`
        }
      }
    },
    edit: {
      widgetWrapper: {
        style:{
          padding: `1.6rem`
        }
      }
    }
  }
}; // could be some default props in this format: { props: {... } }

export const metaConfig = [
    // {
    //   comp: 'TextInput',
    //   isRequired: true,
    //   valuePath: 'props.title',
    //   props: {
    //     label: 'Enter title'
    //   }
    // },
    {
      comp: 'LexicalTextEditor',
      isRequired: true,
      valuePath: 'props.value',
      props: {
        label: 'Enter text'
      }
    },
  ]

  export const ConfigForm = ({
    thisWidgetConfig,
    setConfig,
    underEditWidgetIdx,
  }) => {
  const dataKeys = {
    field2: "props.value",
  };
  
  const queryClient = useQueryClient();
  const allTpls = queryClient.getQueryData("allTpls");

  const currentValue = getVal(thisWidgetConfig, dataKeys.field2);
  const [editedValue, setEditedValue] = useState(currentValue);

  useEffect(() => {
    setEditedValue(currentValue);
  }, [currentValue]);

  const value = editedValue;

  const ct = allTpls.filter((tpl) => tpl.general.segment === "publishing");
  const updateConfigValue = (newValue) => {
    setConfig((prev) => {
      let thisWidgetModifiedConfig = JSON.parse(
        JSON.stringify(prev[underEditWidgetIdx])
      );
      thisWidgetModifiedConfig = setVal(
        thisWidgetModifiedConfig,
        dataKeys.field2,
        newValue
      );
      const modifiedFullConfig = JSON.parse(JSON.stringify(prev));
      modifiedFullConfig[underEditWidgetIdx] = thisWidgetModifiedConfig;
      return modifiedFullConfig;
    });
  };

  const handleChange = (k, v) => {
    setEditedValue(v);
    updateConfigValue(v);
  };

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
      <WidgetHeader icon={"TextAa"} title={"Text"}/>
      <LexicalTextEditor
        id={dataKeys.field2}
        label="Body"
        value={value}
        onChange={handleChange}
        // Matches the rendered widget above, so what you type is what lands on
        // the report.
        v2
      />
    </div>
  );
  };

// Mirrors lexicalTextEditorHasValue in utils/validation/validatorFns, inlined because
// that module imports the app root (queryClient) and pulling it in here creates an
// import cycle that leaves the widget registry empty ("Invalid Component").
export const someReqPropsUnset = (config) => {
  const children = config?.props?.value?.editorState?.root?.children;
  if (!children?.length) return true;
  return children.every((child) => !child.children?.length);
};

export const processRawConfigFn = undefined; // check WidgetBarChart for reference on how to write this.